import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, HelpCircle } from "lucide-react";
import { useChat } from "@/context/ChatContext";

const SS_KEY = "squareBot.welcomePopupSeen";
const AUTO_DISMISS_MS = 15000;

const WelcomePopup = () => {
  const { open, isOpen: chatOpen } = useChat();
  const [visible, setVisible] = useState(false);

  // Show once per browser session, immediately on first page load
  useEffect(() => {
    if (typeof window === "undefined") return;
    let seen = null;
    try {
      seen = sessionStorage.getItem(SS_KEY);
    } catch {
      /* sessionStorage disabled — skip */
    }
    if (seen === "1") return;

    // Show only if chat isn't already open
    if (!chatOpen) setVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-close after 15s
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => dismiss(), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Hide popup if chat is opened by any other means
  useEffect(() => {
    if (chatOpen && visible) setVisible(false);
  }, [chatOpen, visible]);

  const dismiss = () => {
    setVisible(false);
    try {
      sessionStorage.setItem(SS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  const startChat = () => {
    dismiss();
    open();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed z-40"
          data-testid="welcome-popup"
          role="dialog"
          aria-label="Need help?"
          style={{
            right: "clamp(12px, 3vw, 24px)",
            bottom: "clamp(88px, 12vw, 110px)",
            width: "min(340px, calc(100vw - 24px))",
            borderRadius: 16,
            padding: "1.1rem 1.15rem",
            background:
              "linear-gradient(160deg, rgba(20,42,80,0.82), rgba(7,17,39,0.82))",
            border: "1px solid rgba(77,215,238,0.28)",
            backdropFilter: "blur(18px)",
            boxShadow:
              "0 25px 55px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(77,215,238,0.05)",
          }}
        >
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close welcome popup"
            data-testid="welcome-popup-close"
            className="absolute top-2 right-2 w-7 h-7 inline-flex items-center justify-center rounded-md text-[color:var(--text-dim)] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors"
          >
            <X size={14} />
          </button>

          <div className="flex items-start gap-3 pr-6">
            <span
              className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
              style={{
                background: "rgba(77,215,238,0.14)",
                border: "1px solid rgba(77,215,238,0.35)",
                color: "#4dd7ee",
              }}
              aria-hidden
            >
              <HelpCircle size={16} />
            </span>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-white leading-snug">
                Need Help?
              </div>
              <p className="mt-1.5 text-[13px] text-[color:var(--text-dim)] leading-relaxed">
                Have any questions? Ask our{" "}
                <span className="text-[color:var(--cyan-bright)] font-medium">
                  Square Bot
                </span>{" "}
                for instant assistance.
              </p>
            </div>
          </div>

          <div className="mt-4 flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={startChat}
              className="fs-btn-primary text-[13px]"
              style={{ padding: "0.55rem 0.9rem" }}
              data-testid="welcome-popup-primary"
            >
              Open Square Bot
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="fs-btn-outline-soft text-[13px]"
              style={{ padding: "0.55rem 0.9rem" }}
              data-testid="welcome-popup-secondary"
            >
              Continue to Website
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WelcomePopup;
